import React, {
  useState,
  useEffect,
} from "react";

import { AdminSideMenu } from "../admin/AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";


// ============================================================
// TIMEZONE
// ============================================================

const TIMEZONE = "America/Chicago";


// ============================================================
// PAY PERIODS
// ============================================================
// Update these two dates when the payroll calendar changes.
// These presets intentionally use the exact payroll dates and
// are allowed to span partial Monday-Sunday weeks.
// ============================================================

// ============================================================
// PAYROLL PERIOD CONFIGURATION
// ============================================================
//
// Payroll runs every 2 weeks.
//
// September 14, 2026 is the beginning of a known payroll
// period.
//
// Every payroll period after that is exactly 14 days.
//
// Examples:
//
// Sep 14 - Sep 27
// Sep 28 - Oct 11
// Oct 12 - Oct 25
// Oct 26 - Nov 8
//
// The current and previous payroll periods are calculated
// automatically from today's date in America/Chicago.
//
// No payroll dates need to be manually updated.
// ============================================================

const PAYROLL_ANCHOR_DATE = "2026-09-14";

const PAYROLL_PERIOD_DAYS = 14;


// ============================================================
// PAYROLL DATE HELPERS
// ============================================================

const parsePayrollDate = (
  dateKey
) => {

  const [
    year,
    month,
    day,
  ] =
    dateKey
      .split("-")
      .map(Number);

  return new Date(
    Date.UTC(
      year,
      month - 1,
      day
    )
  );

};


const formatPayrollDateKey = (
  date
) => {

  const year =
    date.getUTCFullYear();

  const month =
    String(
      date.getUTCMonth() + 1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      date.getUTCDate()
    ).padStart(
      2,
      "0"
    );

  return `${year}-${month}-${day}`;

};


const addPayrollDays = (
  dateKey,
  days
) => {

  const date =
    parsePayrollDate(
      dateKey
    );

  date.setUTCDate(
    date.getUTCDate() +
    days
  );

  return formatPayrollDateKey(
    date
  );

};


// ============================================================
// GET CURRENT PAYROLL PERIODS
// ============================================================

const getCurrentPayrollPeriods = () => {

  // ----------------------------------------------------------
  // Get today's date in Houston / Chicago time.
  // ----------------------------------------------------------

  const chicagoParts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    ).formatToParts(
      new Date()
    );


  const year =
    chicagoParts.find(
      (part) =>
        part.type === "year"
    )?.value;


  const month =
    chicagoParts.find(
      (part) =>
        part.type === "month"
    )?.value;


  const day =
    chicagoParts.find(
      (part) =>
        part.type === "day"
    )?.value;


  const todayKey =
    `${year}-${month}-${day}`;


  // ----------------------------------------------------------
  // Calculate number of days between the anchor
  // and today.
  // ----------------------------------------------------------

  const anchorDate =
    parsePayrollDate(
      PAYROLL_ANCHOR_DATE
    );

  const todayDate =
    parsePayrollDate(
      todayKey
    );


  const differenceInDays =
    Math.floor(
      (
        todayDate.getTime() -
        anchorDate.getTime()
      ) /
      (1000 * 60 * 60 * 24)
    );


  // ----------------------------------------------------------
  // Determine which 14-day payroll period contains today.
  // ----------------------------------------------------------

  const periodIndex =
    Math.floor(
      differenceInDays /
      PAYROLL_PERIOD_DAYS
    );


  // ----------------------------------------------------------
  // CURRENT PAYROLL
  // ----------------------------------------------------------

  const currentStartDate =
    addPayrollDays(
      PAYROLL_ANCHOR_DATE,
      periodIndex *
        PAYROLL_PERIOD_DAYS
    );


  const currentEndDate =
    addPayrollDays(
      currentStartDate,
      PAYROLL_PERIOD_DAYS - 1
    );


  // ----------------------------------------------------------
  // PREVIOUS PAYROLL
  // ----------------------------------------------------------

  const previousStartDate =
    addPayrollDays(
      currentStartDate,
      -PAYROLL_PERIOD_DAYS
    );


  const previousEndDate =
    addPayrollDays(
      currentStartDate,
      -1
    );


  // ----------------------------------------------------------
  // DISPLAY DATE
  // ----------------------------------------------------------

  const formatDisplayDate =
    (dateKey) => {

      return new Intl.DateTimeFormat(
        "en-US",
        {
          timeZone: "UTC",
          month: "short",
          day: "numeric",
          year: "numeric",
        }
      ).format(
        parsePayrollDate(
          dateKey
        )
      );

    };


  // ----------------------------------------------------------
  // RETURN BOTH PERIODS
  // ----------------------------------------------------------

  return [

    {
      id: "previous",

      label:
        "Previous Payroll",

      range:
        `${formatDisplayDate(
          previousStartDate
        )} – ${formatDisplayDate(
          previousEndDate
        )}`,

      startDate:
        previousStartDate,

      endDate:
        previousEndDate,
    },

    {
      id: "current",

      label:
        "Current Payroll",

      range:
        `${formatDisplayDate(
          currentStartDate
        )} – ${formatDisplayDate(
          currentEndDate
        )}`,

      startDate:
        currentStartDate,

      endDate:
        currentEndDate,
    },

  ];

};

// ============================================================
// FORMAT TIME
// ============================================================

const formatTime = (
  dateValue
) => {

  if (!dateValue) {
    return "-";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      timeZone: TIMEZONE,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }
  ).format(date);

};


// ============================================================
// FORMAT DATE WITH DAY
// ============================================================

const formatDateKeyWithDay = (
  dateKey
) => {

  if (!dateKey) {
    return "-";
  }

  const [
    year,
    month,
    day,
  ] =
    dateKey
      .split("-")
      .map(Number);

  if (
    !year ||
    !month ||
    !day
  ) {
    return dateKey;
  }

  const date =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day,
        12
      )
    );

  return new Intl.DateTimeFormat(
    "en-US",
    {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
    }
  ).format(date);

};


// ============================================================
// FORMAT DATE
// ============================================================

const formatDateKey = (
  dateKey
) => {

  if (!dateKey) {
    return "-";
  }

  const [
    year,
    month,
    day,
  ] =
    dateKey
      .split("-")
      .map(Number);

  if (
    !year ||
    !month ||
    !day
  ) {
    return dateKey;
  }

  return `${month}/${day}/${year}`;

};


// ============================================================
// CALCULATE MINUTES
// ============================================================

const calculateMinutes = (
  clockIn,
  clockOut
) => {

  if (
    !clockIn ||
    !clockOut
  ) {
    return 0;
  }

  const start =
    new Date(clockIn);

  const end =
    new Date(clockOut);

  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    )
  ) {
    return 0;
  }

  const difference =
    end.getTime() -
    start.getTime();

  if (
    difference <= 0
  ) {
    return 0;
  }

  return (
    difference /
    (1000 * 60)
  );

};


// ============================================================
// MINUTES TO HOURS
// ============================================================

const minutesToHours = (minutes) => {
  const totalMinutes =
    Math.max(
      0,
      Math.round(Number(minutes) || 0)
    );

  const hours =
    Math.floor(totalMinutes / 60);

  const remainingMinutes =
    totalMinutes % 60;

  return `${hours}:${String(
    remainingMinutes
  ).padStart(2, "0")}`;
};
// ============================================================
// GET EMPLOYEE NAME
// ============================================================

const getEmployeeName = (
  employee
) => {

  if (!employee) {
    return "Unknown Employee";
  }

  const firstName =
    employee.firstName ||
    "";

  const lastName =
    employee.lastName ||
    "";

  const fullName =
    `${firstName} ${lastName}`.trim();

  return (
    fullName ||
    "Unknown Employee"
  );

};


// ============================================================
// GET STORE NAME
// ============================================================

const getStoreName = (
  store
) => {

  if (!store) {
    return "-";
  }

  if (
    typeof store ===
    "string"
  ) {
    return store;
  }

  return (
    store.name ||
    store.storeName ||
    "-"
  );

};


// ============================================================
// GET STORE ID
// ============================================================

const getStoreId = (
  store
) => {

  if (!store) {
    return "";
  }

  if (
    typeof store ===
    "string"
  ) {
    return store;
  }

  return (
    store._id ||
    store.id ||
    ""
  );

};


// ============================================================
// GET DATE KEY
// ============================================================

const getDateKey = (
  dateValue
) => {

  if (!dateValue) {
    return "";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const parts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    ).formatToParts(date);

  const year =
    parts.find(
      (part) =>
        part.type === "year"
    )?.value;

  const month =
    parts.find(
      (part) =>
        part.type === "month"
    )?.value;

  const day =
    parts.find(
      (part) =>
        part.type === "day"
    )?.value;

  return `${year}-${month}-${day}`;

};


// ============================================================
// CHECK IF MONDAY
// ============================================================

const isMondayDate = (
  dateValue
) => {

  if (!dateValue) {
    return false;
  }

  const [
    year,
    month,
    day,
  ] =
    dateValue
      .split("-")
      .map(Number);

  const date =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day
      )
    );

  return (
    date.getUTCDay() === 1
  );

};


// ============================================================
// CHECK IF SUNDAY
// ============================================================

const isSundayDate = (
  dateValue
) => {

  if (!dateValue) {
    return false;
  }

  const [
    year,
    month,
    day,
  ] =
    dateValue
      .split("-")
      .map(Number);

  const date =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day
      )
    );

  return (
    date.getUTCDay() === 0
  );

};


// ============================================================
// DATETIME LOCAL
// ============================================================

const toDateTimeLocal = (
  dateValue
) => {

  if (!dateValue) {
    return "";
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const parts =
    new Intl.DateTimeFormat(
      "en-CA",
      {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }
    ).formatToParts(date);

  const getPart =
    (type) =>
      parts.find(
        (part) =>
          part.type === type
      )?.value;

  return `${getPart("year")}-${getPart("month")}-${getPart("day")}T${getPart("hour")}:${getPart("minute")}`;

};


// ============================================================
// CHICAGO DATETIME LOCAL TO ISO
// ============================================================

const chicagoDateTimeLocalToISO = (
  dateTimeLocal
) => {

  if (!dateTimeLocal) {
    return null;
  }

  const [
    datePart,
    timePart,
  ] =
    dateTimeLocal.split("T");

  if (
    !datePart ||
    !timePart
  ) {
    return null;
  }

  const [
    year,
    month,
    day,
  ] =
    datePart
      .split("-")
      .map(Number);

  const [
    hour,
    minute,
  ] =
    timePart
      .split(":")
      .map(Number);

  if (
    !year ||
    !month ||
    !day ||
    Number.isNaN(hour) ||
    Number.isNaN(minute)
  ) {
    return null;
  }

  const guess =
    new Date(
      Date.UTC(
        year,
        month - 1,
        day,
        hour,
        minute
      )
    );

  const parts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      }
    ).formatToParts(guess);

  const getPart =
    (type) =>
      parts.find(
        (part) =>
          part.type === type
      )?.value;

  const chicagoYear =
    Number(
      getPart("year")
    );

  const chicagoMonth =
    Number(
      getPart("month")
    );

  const chicagoDay =
    Number(
      getPart("day")
    );

  const chicagoHour =
    Number(
      getPart("hour")
    );

  const chicagoMinute =
    Number(
      getPart("minute")
    );

  const chicagoWallClock =
    Date.UTC(
      chicagoYear,
      chicagoMonth - 1,
      chicagoDay,
      chicagoHour,
      chicagoMinute
    );

  const requestedWallClock =
    Date.UTC(
      year,
      month - 1,
      day,
      hour,
      minute
    );

  const offset =
    chicagoWallClock -
    requestedWallClock;

  return new Date(
    guess.getTime() -
    offset
  ).toISOString();

};


// ============================================================
// PARSE DATETIME LOCAL
// ============================================================

const parseDateTimeLocal = (
  dateTimeLocal
) => {

  if (!dateTimeLocal) {
    return {
      date: "",
      hour: "9",
      minute: "00",
      period: "AM",
    };
  }

  const [
    date,
    time,
  ] =
    dateTimeLocal.split("T");

  if (
    !date ||
    !time
  ) {
    return {
      date: "",
      hour: "9",
      minute: "00",
      period: "AM",
    };
  }

  const [
    hourValue,
    minuteValue,
  ] =
    time.split(":");

  const hour24 =
    Number(hourValue);

  const minute =
    minuteValue || "00";

  if (
    Number.isNaN(hour24)
  ) {
    return {
      date,
      hour: "9",
      minute: "00",
      period: "AM",
    };
  }

  const period =
    hour24 >= 12
      ? "PM"
      : "AM";

  let hour12 =
    hour24 % 12;

  if (
    hour12 === 0
  ) {
    hour12 = 12;
  }

  return {
    date,
    hour:
      String(hour12),
    minute,
    period,
  };

};


// ============================================================
// GET CHICAGO TIME PARTS DIRECTLY FROM DATE
// Used when opening an existing time card for editing.
// This avoids converting through datetime-local first.
// ============================================================

const getChicagoTimeParts = (
  dateValue
) => {

  if (!dateValue) {
    return {
      date: "",
      hour: "",
      minute: "",
      period: "",
    };
  }

  const date =
    new Date(dateValue);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return {
      date: "",
      hour: "",
      minute: "",
      period: "",
    };
  }

  const parts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone: TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }
    ).formatToParts(date);

  const getPart =
    (type) =>
      parts.find(
        (part) =>
          part.type === type
      )?.value || "";

  return {
    date:
      `${getPart("year")}-${getPart("month")}-${getPart("day")}`,

    hour:
      getPart("hour"),

    minute:
      getPart("minute"),

    period:
      getPart("dayPeriod"),
  };

};

// ============================================================
// BUILD DATETIME LOCAL
// ============================================================

const buildDateTimeLocal = (
  date,
  hour,
  minute,
  period
) => {

  if (
    !date ||
    !hour ||
    minute === "" ||
    !period
  ) {
    return "";
  }

  let hourNumber =
    Number(hour);

  if (
    Number.isNaN(
      hourNumber
    )
  ) {
    return "";
  }

  if (
    period === "AM"
  ) {

    if (
      hourNumber === 12
    ) {
      hourNumber = 0;
    }

  } else {

    if (
      hourNumber !== 12
    ) {
      hourNumber += 12;
    }

  }

  const paddedHour =
    String(
      hourNumber
    ).padStart(
      2,
      "0"
    );

  const paddedMinute =
    String(
      minute
    ).padStart(
      2,
      "0"
    );

  return `${date}T${paddedHour}:${paddedMinute}`;

};


// ============================================================
// TIME OPTIONS
// ============================================================

const HOUR_OPTIONS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
];


const MINUTE_OPTIONS =
  Array.from(
    { length: 60 },
    (_, index) =>
      String(index).padStart(
        2,
        "0"
      )
  );


const TIME_SELECT_OPTIONS =
  ["AM", "PM"].flatMap(
    (period) =>
      HOUR_OPTIONS.flatMap(
        (hour) =>
          MINUTE_OPTIONS.map(
            (minute) => ({
              value:
                `${hour}|${minute}|${period}`,
              label:
                `${hour}:${minute} ${period}`,
            })
          )
      )
  );


  const getTimeSelectValue = (
    hour,
    minute,
    period
  ) => {
  
    if (
      !hour ||
      minute === "" ||
      !minute ||
      !period
    ) {
  
      return "";
  
    }
  
    return `${hour}|${minute}|${period}`;
  
  };


// ============================================================
// GROUP ENTRIES BY DATE
// ============================================================

const groupEntriesByDate = (
  entries
) => {

  const grouped = {};

  if (
    !Array.isArray(entries)
  ) {
    return grouped;
  }

  entries.forEach(
    (entry) => {

      const dateKey =
        getDateKey(
          entry.clockIn
        );

      if (!dateKey) {
        return;
      }

      if (
        !grouped[dateKey]
      ) {
        grouped[dateKey] = [];
      }

      grouped[dateKey].push(
        entry
      );

    }
  );

  return grouped;

};


// ============================================================
// GET LOG FOR ENTRY
// ============================================================

const getLogForEntry = (
  logs,
  entryId
) => {

  if (
    !Array.isArray(logs) ||
    !entryId
  ) {
    return null;
  }

  return (
    logs.find(
      (log) =>
        String(
          log?.entryId
        ) ===
        String(
          entryId
        )
    ) || null
  );

};


// ============================================================
// FORMAT DISTANCE
// ============================================================

const formatDistance = (
  distance
) => {

  if (
    distance === null ||
    distance === undefined ||
    distance === ""
  ) {
    return "-";
  }

  const number =
    Number(distance);

  if (
    Number.isNaN(number)
  ) {
    return "-";
  }

  return `${Math.round(number)} m`;

};


// ============================================================
// FORMAT COORDINATE
// ============================================================

const formatCoordinate = (
  value
) => {

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "-";
  }

  const number =
    Number(value);

  if (
    Number.isNaN(number)
  ) {
    return "-";
  }

  return number.toFixed(6);

};


// ============================================================
// EDIT ICON
// ============================================================

const EditIcon = () => (

  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >

    <path d="M12 20h9" />

    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />

  </svg>

);


// ============================================================
// DELETE ICON
// ============================================================

const DeleteIcon = () => (

  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >

    <path d="M3 6h18" />

    <path d="M8 6V4h8v2" />

    <path d="M19 6l-1 14H6L5 6" />

    <path d="M10 11v5" />

    <path d="M14 11v5" />

  </svg>

);


// ============================================================
// MAIN COMPONENT
// ============================================================

export const AdminTimeReportRead = () => {

  const {
    admin
  } =
    useAdminAuthContext();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const PAY_PERIODS =
    getCurrentPayrollPeriods();


  // ==========================================================
  // DATE FILTER
  // ==========================================================

  const [
    startDate,
    setStartDate
  ] =
    useState("");

    // ==========================================================
// DATE FILTER
// ==========================================================

const [
  endDate,
  setEndDate
] =
  useState("");


// ==========================================================
// SELECTED PAY PERIOD
// ==========================================================
// Empty string means the user is working with a custom
// date range instead of one of the payroll presets.
// ==========================================================

const [
  selectedPayPeriodId,
  setSelectedPayPeriodId
] =
  useState("");


// ==========================================================
// EMPLOYEE TIPS
// ==========================================================
//
// Object structure:
//
// {
//   employeeId1: "125.00",
//   employeeId2: "80.00",
//   employeeId3: "150.00"
// }
//
// Tips are entered separately for each employee.
//

const [
  employeeTips,
  setEmployeeTips
] =
  useState({});


    const [
      isDownloading,
      setIsDownloading
    ] = useState(false);

  // ==========================================================
  // REPORT
  // ==========================================================

  const [
    report,
    setReport
  ] =
    useState([]);

  const [
    isLoading,
    setIsLoading
  ] =
    useState(false);

  const [
    error,
    setError
  ] =
    useState("");


  // ==========================================================
  // EXPANDED EMPLOYEES
  // ==========================================================

  const [
    expandedEmployees,
    setExpandedEmployees
  ] =
    useState({});


  // ==========================================================
  // EXPANDED GPS DETAILS
  // ==========================================================

  const [
    expandedDetails,
    setExpandedDetails
  ] =
    useState({});


  // ==========================================================
  // LOCAL ENTRIES
  // ==========================================================

  const [
    localEntries,
    setLocalEntries
  ] =
    useState({});


  // ==========================================================
  // STORES
  // ==========================================================

  const [
    stores,
    setStores
  ] =
    useState([]);

  const [
    storesLoading,
    setStoresLoading
  ] =
    useState(false);


  // ==========================================================
  // TIME CARD MODAL
  // ==========================================================

  const [
    timeCardModal,
    setTimeCardModal
  ] =
    useState({

      isOpen: false,

      mode: "add",

      employeeId: null,

      entryId: null,

      date: "",

      startHour: "9",

      startMinute: "00",

      startPeriod: "AM",

      endHour: "5",

      endMinute: "00",

      endPeriod: "PM",

      storeId: "",

    });


  // ==========================================================
  // ACTION LOADING
  // ==========================================================

  const [
    isSavingTimeCard,
    setIsSavingTimeCard
  ] =
    useState(false);

  const [
    deletingEntryId,
    setDeletingEntryId
  ] =
    useState(null);


  // ==========================================================
  // SORT REPORT
  // ==========================================================

  const sortedReport =
    [...report].sort(
      (a, b) => {

        const nameA =
          getEmployeeName(
            a?.employee
          ).toLowerCase();

        const nameB =
          getEmployeeName(
            b?.employee
          ).toLowerCase();

        return nameA.localeCompare(
          nameB
        );

      }
    );


  // ==========================================================
  // GET EMPLOYEE ENTRIES
  // ==========================================================

  const getEmployeeEntries = (
    employeeReport,
    employeeId
  ) => {

    if (
      localEntries[
        employeeId
      ]
    ) {
      return localEntries[
        employeeId
      ];
    }

    return Array.isArray(
      employeeReport?.entries
    )
      ? employeeReport.entries
      : [];

  };


  // ==========================================================
  // INITIALIZE LOCAL ENTRIES
  // ==========================================================

  useEffect(
    () => {

      if (
        report.length === 0
      ) {
        return;
      }

      setLocalEntries(
        (previous) => {

          const updated =
            {
              ...previous
            };

          report.forEach(
            (
              employeeReport,
              index
            ) => {

              const employeeId =
                employeeReport
                  ?.employee
                  ?._id ||
                `employee-${index}`;

              if (
                !updated[
                  employeeId
                ]
              ) {

                updated[
                  employeeId
                ] =
                  Array.isArray(
                    employeeReport?.entries
                  )
                    ? [
                        ...employeeReport.entries
                      ]
                    : [];

              }

            }
          );

          return updated;

        }
      );

    },
    [report]
  );


  // ==========================================================
  // FETCH STORES
  // ==========================================================

  const fetchStores =
    async () => {

      try {

        if (
          !admin?.token
        ) {
          return;
        }

        setStoresLoading(
          true
        );

        const response =
          await fetch(
            API_URL.ADMIN_STORE_LIST,
            {
              method: "GET",

              headers: {
                Authorization:
                  `Bearer ${admin.token}`,
              },
            }
          );

        const data =
          await response.json();

        if (
          !response.ok
        ) {

          throw new Error(
            data.error ||
            data.message ||
            "Failed to load stores."
          );

        }

        const storeData =
          Array.isArray(data)
            ? data
            : Array.isArray(data.stores)
              ? data.stores
              : Array.isArray(data.store)
                ? data.store
                : Array.isArray(data.data)
                  ? data.data
                  : [];

        setStores(
          storeData
        );

      } catch (
        err
      ) {

        console.error(
          "STORE LIST ERROR:",
          err
        );

      } finally {

        setStoresLoading(
          false
        );

      }

    };


  // ==========================================================
  // LOAD STORES
  // ==========================================================

  useEffect(
    () => {

      if (
        admin?.token
      ) {
        fetchStores();
      }

    },
    [admin?.token]
  );



// ==========================================================
// UPDATE EMPLOYEE TIPS
// ==========================================================

const updateEmployeeTips = (
  employeeId,
  value
) => {

  setEmployeeTips(
    (previous) => ({
      ...previous,

      [employeeId]:
        value,
    })
  );

  setError("");
};


  // ==========================================================
  // FETCH REPORT
  // ==========================================================

  const fetchTimeReport =
    async ({
      startDateOverride = null,
      endDateOverride = null,
      isPayPeriod = false,
    } = {}) => {

      const requestedStartDate =
        startDateOverride || startDate;

      const requestedEndDate =
        endDateOverride || endDate;

      const activePayPeriod =
        PAY_PERIODS.find(
          (payPeriod) =>
            payPeriod.id ===
              selectedPayPeriodId &&
            payPeriod.startDate ===
              requestedStartDate &&
            payPeriod.endDate ===
              requestedEndDate
        );

      const allowPayPeriodRange =
        isPayPeriod ||
        Boolean(activePayPeriod);

      try {

        setError("");

        if (
          !requestedStartDate ||
          !requestedEndDate
        ) {

          setError(
            "Please select a start date and end date."
          );

          return;

        }

        if (
          requestedStartDate >
          requestedEndDate
        ) {

          setError(
            "Start date cannot be after end date."
          );

          return;

        }

        // Custom ranges keep the existing Monday-to-Sunday
        // validation. Preset payroll periods use their exact
        // payroll dates and therefore bypass this UI validation.
        if (!allowPayPeriodRange) {

          if (
            !isMondayDate(
              requestedStartDate
            )
          ) {

            setError(
              "Start date must be a Monday."
            );

            return;

          }

          if (
            !isSundayDate(
              requestedEndDate
            )
          ) {

            setError(
              "End date must be a Sunday."
            );

            return;

          }

        }

        if (
          !admin?.token
        ) {

          setError(
            "You are not authorized. Please log in again."
          );

          return;

        }

        setIsLoading(
          true
        );

        const response =
          await fetch(

            API_URL.ADMIN_TIMEREPORT_GET +
              `?startDate=${encodeURIComponent(
                requestedStartDate
              )}&endDate=${encodeURIComponent(
                requestedEndDate
              )}`,

            {
              method: "GET",

              headers: {
                Authorization:
                  `Bearer ${admin.token}`,
              },
            }

          );

        const data =
          await response.json();

        if (
          !response.ok
        ) {

          throw new Error(
            data.error ||
            data.message ||
            "Failed to generate time report"
          );

        }

        const reportData =
          Array.isArray(
            data.report
          )
            ? data.report
            : [];

        const expandedEmployeeState = {};

        reportData.forEach(
          (
            employeeReport,
            index
          ) => {

            const employeeId =
              employeeReport
                ?.employee
                ?._id ||
              `employee-${index}`;

            expandedEmployeeState[
              employeeId
            ] = true;

          }
        );

        setExpandedEmployees(
          expandedEmployeeState
        );

        setExpandedDetails(
          {}
        );

        setLocalEntries(
          {}
        );

        setEmployeeTips(
          {}
        );

        setReport(
          reportData
        );

      } catch (
        err
      ) {

        console.error(
          "TIME REPORT ERROR:",
          err
        );

        setReport(
          []
        );

        setLocalEntries(
          {}
        );

        setExpandedEmployees(
          {}
        );

        setExpandedDetails(
          {}
        );

        setError(
          err.message ||
          "Failed to generate time report"
        );

      } finally {

        setIsLoading(
          false
        );

      }

    };


  // ==========================================================
  // SELECT PAY PERIOD
  // ==========================================================

  const selectPayPeriod = (
    payPeriod
  ) => {

    setSelectedPayPeriodId(
      payPeriod.id
    );

    setStartDate(
      payPeriod.startDate
    );

    setEndDate(
      payPeriod.endDate
    );

    setError("");

    fetchTimeReport({
      startDateOverride:
        payPeriod.startDate,
      endDateOverride:
        payPeriod.endDate,
      isPayPeriod: true,
    });

  };


  // ==========================================================
  // TOGGLE EMPLOYEE
  // ==========================================================

  const toggleEmployee = (
    employeeId
  ) => {

    setExpandedEmployees(
      (previous) => ({
        ...previous,

        [employeeId]:
          !previous[
            employeeId
          ],
      })
    );

  };

  // ==========================================================
// EXPAND ALL EMPLOYEES
// ==========================================================

const expandAllEmployees = () => {

  const expandedState = {};

  sortedReport.forEach(
    (
      employeeReport,
      index
    ) => {

      const employeeId =
        employeeReport?.employee?._id ||
        `employee-${index}`;

      expandedState[
        employeeId
      ] = true;

    }
  );

  setExpandedEmployees(
    expandedState
  );

};


// ==========================================================
// COLLAPSE ALL EMPLOYEES
// ==========================================================

const collapseAllEmployees = () => {

  const collapsedState = {};

  sortedReport.forEach(
    (
      employeeReport,
      index
    ) => {

      const employeeId =
        employeeReport?.employee?._id ||
        `employee-${index}`;

      collapsedState[
        employeeId
      ] = false;

    }
  );

  setExpandedEmployees(
    collapsedState
  );

};


  // ==========================================================
  // TOGGLE GPS DETAILS
  // ==========================================================

  const toggleDetails = (
    entryId
  ) => {

    setExpandedDetails(
      (previous) => ({
        ...previous,

        [entryId]:
          !previous[
            entryId
          ],
      })
    );

  };


  // ==========================================================
  // OPEN ADD TIME CARD
  // ==========================================================

  const openAddTimeCard = (
    employeeId,
    dateKey = ""
  ) => {

    setTimeCardModal({

      isOpen: true,

      mode: "add",

      employeeId,

      entryId: null,

      date:
        dateKey ||
        "",

      startHour: "9",

      startMinute: "00",

      startPeriod: "AM",

      endHour: "5",

      endMinute: "00",

      endPeriod: "PM",

      storeId: "",

    });

  };


// ==========================================================
// OPEN EDIT TIME CARD
// ==========================================================

// ============================================================
// OPEN EDIT TIME CARD
// ============================================================

const openEditTimeCard = (
  employeeId,
  entry
) => {

  // ==========================================================
  // CLOCK IN
  // ==========================================================
  //
  // Read the existing Clock In directly in Chicago time.
  // This prevents the existing datetime-local conversion
  // from changing the displayed start time.
  // ==========================================================

  const clockIn =
    getChicagoTimeParts(
      entry.clockIn
    );


  // ==========================================================
  // CLOCK OUT
  // ==========================================================
  //
  // If the employee has not clocked out yet, keep all End
  // fields completely empty.
  //
  // This preserves the existing "Not clocked out" behavior.
  // ==========================================================

  const hasClockOut =
    Boolean(
      entry.clockOut
    );


  const clockOut =
    hasClockOut
      ? getChicagoTimeParts(
          entry.clockOut
        )
      : {
          date: "",
          hour: "",
          minute: "",
          period: "",
        };


  // ==========================================================
  // OPEN MODAL
  // ==========================================================

  setTimeCardModal({

    isOpen: true,

    mode: "edit",

    employeeId,

    entryId:
      entry._id,

    // --------------------------------------------------------
    // DATE
    // --------------------------------------------------------

    date:
      clockIn.date,

    // --------------------------------------------------------
    // START
    // --------------------------------------------------------

    startHour:
      clockIn.hour,

    startMinute:
      clockIn.minute,

    startPeriod:
      clockIn.period,

    // --------------------------------------------------------
    // END
    // --------------------------------------------------------

    endHour:
      clockOut.hour,

    endMinute:
      clockOut.minute,

    endPeriod:
      clockOut.period,

    // --------------------------------------------------------
    // STORE
    // --------------------------------------------------------

    storeId:
      getStoreId(
        entry.store
      ),

  });

};

  // ==========================================================
  // CLOSE MODAL
  // ==========================================================

  const closeTimeCardModal =
    () => {

      if (
        isSavingTimeCard
      ) {
        return;
      }

      setTimeCardModal({

        isOpen: false,

        mode: "add",

        employeeId: null,

        entryId: null,

        date: "",

        startHour: "9",

        startMinute: "00",

        startPeriod: "AM",

        endHour: "5",

        endMinute: "00",

        endPeriod: "PM",

        storeId: "",

      });

    };


// ==========================================================
// SAVE TIME CARD
// ==========================================================

const saveTimeCard =
  async () => {

    const {
      employeeId,
      mode,
      entryId,
      date,
      startHour,
      startMinute,
      startPeriod,
      endHour,
      endMinute,
      endPeriod,
      storeId,
    } =
      timeCardModal;


    // ========================================================
    // BUILD CLOCK IN
    // ========================================================

    const clockIn =
      buildDateTimeLocal(
        date,
        startHour,
        startMinute,
        startPeriod
      );


    // ========================================================
    // BUILD CLOCK OUT
    // ========================================================
    //
    // Clock Out can be empty when editing an open entry.
    // ========================================================

    const clockOut =
      buildDateTimeLocal(
        date,
        endHour,
        endMinute,
        endPeriod
      );


    // ========================================================
    // ADD MODE
    // ========================================================
    //
    // Adding a brand-new historical time entry still requires
    // BOTH Clock In and Clock Out.
    //
    // This keeps the existing Add Time behavior unchanged.
    // ========================================================

    if (
      mode === "add"
    ) {

      if (
        !clockIn ||
        !clockOut
      ) {

        alert(
          "Please select both times."
        );

        return;

      }

    }


    // ========================================================
    // EDIT MODE
    // ========================================================
    //
    // Clock In is always required.
    //
    // Clock Out is optional when editing.
    //
    // This allows:
    //
    // 9:00 AM -> NULL
    //
    // to become:
    //
    // 8:45 AM -> NULL
    //
    // without requiring the employee to clock out first.
    // ========================================================

    if (
      mode === "edit"
    ) {

      if (
        !clockIn
      ) {

        alert(
          "Please select a Clock In time."
        );

        return;

      }

    }


    // ========================================================
    // STORE
    // ========================================================

    if (
      !storeId
    ) {

      alert(
        "Please select a store."
      );

      return;

    }


    // ========================================================
    // ADMIN AUTH
    // ========================================================

    if (
      !admin?.token
    ) {

      alert(
        "You are not authorized. Please log in again."
      );

      return;

    }


    // ========================================================
    // CONVERT CLOCK IN
    // ========================================================

    const clockInISO =
      chicagoDateTimeLocalToISO(
        clockIn
      );


    if (
      !clockInISO
    ) {

      alert(
        "Invalid Clock In date or time."
      );

      return;

    }


    const clockInDate =
      new Date(
        clockInISO
      );


    if (
      Number.isNaN(
        clockInDate.getTime()
      )
    ) {

      alert(
        "Invalid Clock In date or time."
      );

      return;

    }


    // ========================================================
    // CONVERT CLOCK OUT
    // ========================================================
    //
    // Only convert Clock Out when one exists.
    // ========================================================

    let clockOutISO =
      null;

    let clockOutDate =
      null;


    if (
      clockOut
    ) {

      clockOutISO =
        chicagoDateTimeLocalToISO(
          clockOut
        );


      if (
        !clockOutISO
      ) {

        alert(
          "Invalid Clock Out date or time."
        );

        return;

      }


      clockOutDate =
        new Date(
          clockOutISO
        );


      if (
        Number.isNaN(
          clockOutDate.getTime()
        )
      ) {

        alert(
          "Invalid Clock Out date or time."
        );

        return;

      }

    }


    // ========================================================
    // VALIDATE TIME ORDER
    // ========================================================
    //
    // Only validate when Clock Out exists.
    //
    // An open entry is valid:
    //
    // Clock In: 9:00 AM
    // Clock Out: Not clocked out
    // ========================================================

    if (
      clockOutDate &&
      clockOutDate <=
        clockInDate
    ) {

      alert(
        "End time must be after start time."
      );

      return;

    }


    // ========================================================
    // SAVE
    // ========================================================

    try {

      setIsSavingTimeCard(
        true
      );

      setError("");


      // ======================================================
      // ADD
      // ======================================================

      if (
        mode === "add"
      ) {

        const response =
          await fetch(
            API_URL.ADMIN_TIMEREPORT_ADD,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${admin.token}`,
              },

              body:
                JSON.stringify({

                  employeeId,

                  storeId,

                  clockIn,

                  clockOut,

                }),

            }
          );


        const data =
          await response.json();


        if (
          !response.ok
        ) {

          throw new Error(
            data.error ||
            data.message ||
            "Failed to add time entry."
          );

        }


        closeTimeCardModal();

        await fetchTimeReport();

        return;

      }


      // ======================================================
      // EDIT
      // ======================================================

      if (
        !entryId
      ) {

        throw new Error(
          "Time entry ID is missing."
        );

      }


      // ======================================================
      // EDIT REQUEST
      // ======================================================
      //
      // For an open entry:
      //
      // clockOut = null
      //
      // For a completed entry:
      //
      // clockOut = selected Clock Out
      //
      // The backend now supports both.
      // ======================================================

      const response =
        await fetch(
          API_URL.ADMIN_TIMEREPORT_EDIT +
            entryId,
          {
            method: "PUT",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${admin.token}`,
            },

            body:
              JSON.stringify({

                storeId,

                clockIn,

                clockOut:
                  clockOut || null,

              }),

          }
        );


      const data =
        await response.json();


      if (
        !response.ok
      ) {

        throw new Error(
          data.error ||
          data.message ||
          "Failed to update time entry."
        );

      }


      closeTimeCardModal();

      await fetchTimeReport();


    } catch (
      err
    ) {

      console.error(
        "SAVE TIME CARD ERROR:",
        err
      );


      alert(
        err.message ||
        "Failed to save time card."
      );


    } finally {

      setIsSavingTimeCard(
        false
      );

    }

  };

  // ==========================================================
  // DELETE TIME CARD
  // ==========================================================

  const deleteTimeCard = async (
    employeeId,
    entryId
  ) => {

    if (
      !entryId
    ) {

      alert(
        "Time entry ID is missing."
      );

      return;

    }

    const confirmed =
      window.confirm(
        "Are you sure you want to delete this time card?"
      );

    if (
      !confirmed
    ) {
      return;
    }

    if (
      !admin?.token
    ) {

      alert(
        "You are not authorized. Please log in again."
      );

      return;

    }

    try {

      setDeletingEntryId(
        entryId
      );

      setError("");

      const response =
        await fetch(
          API_URL.ADMIN_TIMEREPORT_DELETE +
            entryId,
          {
            method: "DELETE",

            headers: {
              Authorization:
                `Bearer ${admin.token}`,
            },
          }
        );

      const data =
        await response.json();

      if (
        !response.ok
      ) {

        throw new Error(
          data.error ||
          data.message ||
          "Failed to delete time entry."
        );

      }

      await fetchTimeReport();

    } catch (
      err
    ) {

      console.error(
        "DELETE TIME CARD ERROR:",
        err
      );

      alert(
        err.message ||
        "Failed to delete time card."
      );

    } finally {

      setDeletingEntryId(
        null
      );

    }

  };

// ==========================================================
// DOWNLOAD TIME REPORT
// ==========================================================

// ==========================================================
// DOWNLOAD TIME REPORT
// ==========================================================

const downloadTimeReport =
  async () => {

    try {

      // ======================================================
      // VALIDATION
      // ======================================================

      if (
        !startDate ||
        !endDate
      ) {

        alert(
          "Please select a start date and end date."
        );

        return;
      }


      if (
        startDate >
        endDate
      ) {

        alert(
          "Start date cannot be after end date."
        );

        return;
      }


      const isSelectedPreset =
        PAY_PERIODS.some(
          (payPeriod) =>
            payPeriod.id ===
            selectedPayPeriodId &&
            payPeriod.startDate ===
              startDate &&
            payPeriod.endDate ===
              endDate
        );

      if (!isSelectedPreset) {

        if (
          !isMondayDate(
            startDate
          )
        ) {

          alert(
            "Start date must be a Monday."
          );

          return;
        }

        if (
          !isSundayDate(
            endDate
          )
        ) {

          alert(
            "End date must be a Sunday."
          );

          return;
        }

      }


      if (
        !admin?.token
      ) {

        alert(
          "You are not authorized. Please log in again."
        );

        return;
      }


      // ======================================================
      // BUILD EMPLOYEE TIPS OBJECT
      // ======================================================
      //
      // Only employees currently shown in the report
      // are included.
      //
      // Blank tips = 0.
      //

      const tipsForExport = {};

      sortedReport.forEach(
        (
          employeeReport,
          employeeIndex
        ) => {

          const employeeId =
            employeeReport?.employee?._id ||
            `employee-${employeeIndex}`;

          const rawTips =
            employeeTips[
              employeeId
            ];

          const numericTips =
            rawTips === "" ||
            rawTips === undefined ||
            rawTips === null
              ? 0
              : Number(rawTips);


          // --------------------------------------------------
          // VALIDATE
          // --------------------------------------------------

          if (
            !Number.isFinite(
              numericTips
            ) ||
            numericTips < 0
          ) {

            throw new Error(
              `Tips for ${getEmployeeName(
                employeeReport?.employee
              )} must be a valid non-negative amount.`
            );
          }


          // --------------------------------------------------
          // ROUND TO CENTS
          // --------------------------------------------------

          tipsForExport[
            employeeId
          ] =
            Math.round(
              numericTips * 100
            ) / 100;

        }
      );


      // ======================================================
      // SET DOWNLOADING
      // ======================================================

      setIsDownloading(
        true
      );


      // ======================================================
      // ENCODE EMPLOYEE TIPS
      // ======================================================

      const encodedEmployeeTips =
        encodeURIComponent(
          JSON.stringify(
            tipsForExport
          )
        );


      // ======================================================
      // BUILD DOWNLOAD URL
      // ======================================================

      const downloadUrl =
        API_URL.ADMIN_TIMEREPORT_DOWNLOAD +
        `?startDate=${encodeURIComponent(
          startDate
        )}` +
        `&endDate=${encodeURIComponent(
          endDate
        )}` +
        `&employeeTips=${encodedEmployeeTips}`;


      // ======================================================
      // REQUEST EXCEL FILE
      // ======================================================

      const response =
        await fetch(
          downloadUrl,
          {
            method:
              "GET",

            headers: {
              Authorization:
                `Bearer ${admin.token}`,
            },

          }
        );


      // ======================================================
      // ERROR
      // ======================================================

      if (
        !response.ok
      ) {

        let message =
          "Failed to download report.";


        try {

          const contentType =
            response.headers.get(
              "content-type"
            );


          if (
            contentType &&
            contentType.includes(
              "application/json"
            )
          ) {

            const data =
              await response.json();

            message =
              data.error ||
              data.message ||
              message;
          }

        } catch (
          error
        ) {

          console.error(
            "DOWNLOAD ERROR RESPONSE:",
            error
          );

        }


        throw new Error(
          message
        );
      }


      // ======================================================
      // GET EXCEL BLOB
      // ======================================================

      const blob =
        await response.blob();


      if (
        !blob ||
        blob.size === 0
      ) {

        throw new Error(
          "The downloaded report is empty."
        );
      }


      // ======================================================
      // CREATE DOWNLOAD URL
      // ======================================================

      const url =
        window.URL.createObjectURL(
          blob
        );


      // ======================================================
      // CREATE DOWNLOAD LINK
      // ======================================================

      const link =
        document.createElement(
          "a"
        );


      link.href =
        url;


      link.download =
        `payroll-${startDate}-to-${endDate}.xlsx`;


      link.style.display =
        "none";


      document.body.appendChild(
        link
      );


      // ======================================================
      // DOWNLOAD
      // ======================================================

      link.click();


      // ======================================================
      // CLEANUP
      // ======================================================

      document.body.removeChild(
        link
      );


      window.URL.revokeObjectURL(
        url
      );


    } catch (
      error
    ) {

      console.error(
        "DOWNLOAD TIME REPORT ERROR:",
        error
      );


      alert(
        error.message ||
        "Failed to download report."
      );


    } finally {

      setIsDownloading(
        false
      );

    }

  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <div className="flex h-dvh min-h-0 bg-gray-100">

      {/* Desktop navigation */}
      <div className="hidden md:block shrink-0">
        <AdminSideMenu />
      </div>

      {/* Mobile navigation drawer. Uses the existing side menu so its
          navigation links and logout action remain the same. */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute inset-0 bg-black/50"
          />
          <div className="absolute inset-y-0 left-0 z-10 w-[min(18rem,85vw)] overflow-y-auto bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <span className="font-semibold text-gray-800">Menu</span>
              <button
                type="button"
                aria-label="Close navigation menu"
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <AdminSideMenu />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 min-h-0 p-3 sm:p-4 md:p-6 bg-gray-100 pt-4 md:pt-16 flex flex-col overflow-hidden">


        {/* ====================================================
            HEADER
        ==================================================== */}

        <div className="mb-3 sm:mb-4 flex items-start justify-between gap-3">

          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              Time Report
            </h1>

            <p className="text-sm text-gray-500 mt-1">
              Employee time cards and actual working hours
            </p>
          </div>

          <button
            type="button"
            aria-label="Open navigation menu"
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

        </div>


{/* ====================================================
    PAY PERIOD + DATE FILTER
==================================================== */}

<div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 sm:p-4 md:p-5 mb-4 sm:mb-5">

  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 sm:gap-3 items-end">

    {/* ==================================================
        START DATE
    ================================================== */}

    <div className="hidden sm:block min-w-0 lg:col-span-1">

      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
        Start Date
      </label>

      <input
        type="date"
        value={startDate}
        onChange={(e) => {

          setStartDate(
            e.target.value
          );

          setSelectedPayPeriodId(
            ""
          );

          setError("");

        }}
        className="block w-full min-w-0 max-w-full box-border appearance-none h-10 sm:h-11 px-1.5 sm:px-3 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
      />

    </div>


    {/* ==================================================
        END DATE
    ================================================== */}

    <div className="hidden sm:block min-w-0 lg:col-span-1">

      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
        End Date
      </label>

      <input
        type="date"
        value={endDate}
        onChange={(e) => {

          setEndDate(
            e.target.value
          );

          setSelectedPayPeriodId(
            ""
          );

          setError("");

        }}
        className="block w-full min-w-0 max-w-full box-border appearance-none h-10 sm:h-11 px-1.5 sm:px-3 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
      />

    </div>


    {/* ==================================================
        PAYROLL PERIODS
    ================================================== */}

    <div className="col-span-1 sm:col-span-2 min-w-0 lg:col-span-2">

      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
        Payroll Period
      </label>

      <div className="grid grid-cols-2 gap-2">

        {/* PREVIOUS PAYROLL */}

        <button
          type="button"
          onClick={() => selectPayPeriod(PAY_PERIODS[0])}
          disabled={isLoading || isDownloading}
          className={`w-full min-w-0 h-11 px-2 sm:px-3 rounded-lg border text-left transition disabled:opacity-50 ${
            selectedPayPeriodId === PAY_PERIODS[0].id &&
            startDate === PAY_PERIODS[0].startDate &&
            endDate === PAY_PERIODS[0].endDate
              ? "bg-slate-800 border-slate-800 text-white"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <span className="block text-[10px] font-bold uppercase tracking-wide leading-none">
            Previous Payroll
          </span>
          <span className="block text-[10px] sm:text-xs font-semibold mt-1 truncate">
            {PAY_PERIODS[0].range}
          </span>
        </button>

        {/* CURRENT PAYROLL */}

        <button
          type="button"
          onClick={() => selectPayPeriod(PAY_PERIODS[1])}
          disabled={isLoading || isDownloading}
          className={`w-full min-w-0 h-11 px-2 sm:px-3 rounded-lg border text-left transition disabled:opacity-50 ${
            selectedPayPeriodId === PAY_PERIODS[1].id &&
            startDate === PAY_PERIODS[1].startDate &&
            endDate === PAY_PERIODS[1].endDate
              ? "bg-slate-800 border-slate-800 text-white"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <span className="block text-[10px] font-bold uppercase tracking-wide leading-none">
            Current Payroll
          </span>
          <span className="block text-[10px] sm:text-xs font-semibold mt-1 truncate">
            {PAY_PERIODS[1].range}
          </span>
        </button>

      </div>

    </div>


    {/* ==================================================
        GENERATE REPORT
    ================================================== */}

    <div className="hidden sm:block lg:col-span-1">

      <label className="hidden lg:block text-xs sm:text-sm font-medium text-transparent mb-1">
        Action
      </label>

      <button
        type="button"
        onClick={() =>
          fetchTimeReport()
        }
        disabled={
          isLoading ||
          isDownloading
        }
        className="w-full min-w-0 h-10 sm:h-11 px-2 sm:px-4 bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-xs sm:text-sm whitespace-nowrap"
      >

        {isLoading
          ? "Loading..."
          : "Generate Report"}

      </button>

    </div>


    {/* ==================================================
        DOWNLOAD EXCEL
    ================================================== */}

    <div className="hidden sm:block lg:col-span-1">

      <label className="hidden lg:block text-xs sm:text-sm font-medium text-transparent mb-1">
        Action
      </label>

      <button
        type="button"
        onClick={
          downloadTimeReport
        }
        disabled={
          isDownloading ||
          isLoading ||
          report.length === 0
        }
        className="w-full min-w-0 h-10 sm:h-11 px-2 sm:px-4 bg-green-700 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-xs sm:text-sm whitespace-nowrap"
      >

        {isDownloading
          ? "Downloading..."
          : "Download Excel"}

      </button>

    </div>

  </div>


  {/* ====================================================
      CUSTOM RANGE HELP
  ==================================================== */}

  <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 mt-2.5">

    <p className="text-xs text-gray-400">
      Custom reports must start Monday and end Sunday.
    </p>

    {startDate &&
      endDate && (

        <p className="text-xs text-gray-500">
          Selected:{" "}
          {formatDateKey(
            startDate
          )}{" "}
          →{" "}
          {formatDateKey(
            endDate
          )}
        </p>

      )}

  </div>


  {/* ====================================================
      ERROR
  ==================================================== */}

  {error && (

    <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
      {error}
    </div>

  )}

</div>

        {/* ====================================================
            REPORT
        ==================================================== */}

        <div className="flex-1 overflow-auto pb-6">


          {isLoading && (

            <div className="bg-white border rounded-lg p-8 text-center">

              <p className="text-gray-600">
                Loading time report...
              </p>

            </div>

          )}


          {!isLoading &&
            report.length === 0 &&
            !error && (

              <div className="bg-white border rounded-lg p-8 text-center">

                <p className="text-gray-500">

                  Select a payroll period above, or choose a custom Monday-to-Sunday date range.

                </p>

              </div>

            )}


          {!isLoading &&
            sortedReport.length > 0 && (

              <div className="space-y-4">

                  <div className="flex flex-wrap justify-end gap-2 mb-3">

                  <button
                    type="button"
                    onClick={expandAllEmployees}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    Expand All
                  </button>

                  <button
                    type="button"
                    onClick={collapseAllEmployees}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    Collapse All
                  </button>

                  </div>


                {sortedReport.map(
                  (
                    employeeReport,
                    employeeIndex
                  ) => {

                    const employee =
                      employeeReport?.employee;

                    const employeeId =
                      employee?._id ||
                      `employee-${employeeIndex}`;

                    const employeeName =
                      getEmployeeName(
                        employee
                      );

                    const entries =
                      getEmployeeEntries(
                        employeeReport,
                        employeeId
                      );

                    const logs =
                      Array.isArray(
                        employeeReport?.logs
                      )
                        ? employeeReport.logs
                        : [];

                    const sortedEntries =
                      [
                        ...entries
                      ].sort(
                        (a, b) =>
                          new Date(
                            a.clockIn
                          ) -
                          new Date(
                            b.clockIn
                          )
                      );

                    const totalActualMinutes =
                      sortedEntries.reduce(
                        (
                          total,
                          entry
                        ) =>
                          total +
                          calculateMinutes(
                            entry.clockIn,
                            entry.clockOut
                          ),
                        0
                      );

                      const totalActualHours =
                      minutesToHours(
                        totalActualMinutes
                      );
                      
                    const groupedEntries =
                      groupEntriesByDate(
                        sortedEntries
                      );

                    const dateKeys =
                      Object.keys(
                        groupedEntries
                      ).sort();

                    const isExpanded =
                      expandedEmployees[
                        employeeId
                      ] === true;


                    return (

                      <div
                        key={employeeId}
                        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                      >


                        {/* ==========================================
                            EMPLOYEE HEADER
                        ========================================== */}

                        <div
                          className="px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition"
                          onClick={
                            () =>
                              toggleEmployee(
                                employeeId
                              )
                          }
                        >

                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-6">


                            {/* LEFT */}

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-6 min-w-0 w-full lg:w-auto">

                              <h2 className="text-base sm:text-xl font-bold text-gray-800 truncate">

                                {employeeName}

                              </h2>


                              <div className="flex items-center gap-1.5 sm:gap-2 text-gray-500 text-xs sm:text-sm">

                                <span>
                                  Time cards
                                </span>

                                <span className="font-bold text-gray-800 text-base sm:text-lg">

                                  {
                                    sortedEntries.length
                                  }

                                </span>

                              </div>


                              <div className="flex items-center gap-1.5 sm:gap-2 text-gray-500 text-xs sm:text-sm">

                                <span>
                                  Actual hours
                                </span>

                                <span className="font-bold text-gray-800 text-base sm:text-lg">

                                  {
                                    totalActualHours
                                  }

                                </span>

                              </div>

                            </div>


                            {/* RIGHT */}

                           {/* RIGHT */}
<div
  className="flex flex-wrap items-center gap-2 sm:gap-4 w-full lg:w-auto"
  onClick={
    (event) =>
      event.stopPropagation()
  }
>


  {/* ==========================================
      TIPS
  ========================================== */}

  <div
    className="flex items-center gap-2"
  >

    <label
      htmlFor={`tips-${employeeId}`}
      className="text-sm font-semibold text-gray-600 whitespace-nowrap"
    >
      Tips
    </label>


    <div
      className="relative"
    >

      <span
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm"
      >
        $
      </span>


      <input
  id={`tips-${employeeId}`}
  type="number"
  min="0"
  step="0.01"
  value={
    employeeTips[
      employeeId
    ] ?? ""
  }
  onChange={
    (event) =>
      updateEmployeeTips(
        employeeId,
        event.target.value
      )
  }
  onClick={
    (event) =>
      event.stopPropagation()
  }
  onKeyDown={
    (event) =>
      event.stopPropagation()
  }
  placeholder="0.00"
  className="w-24 sm:w-28 pl-7 pr-2 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
/>

    </div>

  </div>


  {/* ==========================================
      ADD TIME
  ========================================== */}

  <button
    type="button"
    onClick={
      (event) => {

        event.stopPropagation();

        openAddTimeCard(
          employeeId
        );

      }
    }
    className="px-3 sm:px-5 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-700 font-semibold text-sm whitespace-nowrap"
  >
    + Add Time
  </button>


  {/* ==========================================
      EXPAND / COLLAPSE
  ========================================== */}

  <button
    type="button"
    onClick={
      (event) => {

        event.stopPropagation();

        toggleEmployee(
          employeeId
        );

      }
    }
    className="text-gray-500 text-sm px-1"
    title={
      isExpanded
        ? "Collapse employee"
        : "Expand employee"
    }
  >
    {
      isExpanded
        ? "▲"
        : "▼"
    }
  </button>

</div>

                          </div>

                        </div>


                        {/* ==========================================
                            EXPANDED TIME LIST
                        ========================================== */}

                        {isExpanded && (

                          <div className="overflow-x-hidden">


                            {/* ======================================
                                COLUMN HEADERS
                            ====================================== */}

                          <div className="hidden md:grid grid-cols-[1.2fr_1.4fr_0.7fr_1.2fr_100px] gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">
                              <div>
                                Date
                              </div>

                              <div>
                                Time
                              </div>

                              <div>
                                Hours
                              </div>

                              <div>
                                Store
                              </div>

                              <div>
                                Actions
                              </div>

                            </div>


                            {dateKeys.length === 0 && (

                              <div className="p-8 text-center text-gray-500">

                                No time cards found.

                              </div>

                            )}


                            {/* ======================================
                                DATE GROUPS
                            ====================================== */}

                            {dateKeys.map(
                              (
                                dateKey
                              ) => {

                                const dayEntries =
                                  groupedEntries[
                                    dateKey
                                  ];


                                return (

                                  <React.Fragment
                                    key={dateKey}
                                  >

                                    {dayEntries.map(
                                      (
                                        entry,
                                        entryIndex
                                      ) => {

                                        const entryMinutes =
                                        calculateMinutes(
                                          entry.clockIn,
                                          entry.clockOut
                                        );
                                      
                                      const entryHours =
                                        minutesToHours(
                                          entryMinutes
                                        );
                                      
                                      
                                      // ====================================
                                      // RED FLAG
                                      //
                                      // 1. Missing clock-out
                                      // 2. Less than 2 hours
                                      // 3. More than 10 hours
                                      //
                                      // Use minutes directly so values such as
                                      // "25:00" are evaluated correctly.
                                      // ====================================
                                      
                                      const isMissingClockOut =
                                        !entry.clockOut;
                                      
                                      const isLessThanTwoHours =
                                        entryMinutes < 120;
                                      
                                      const isMoreThanTenHours =
                                        entryMinutes > 600;
                                      
                                      const isFlagged =
                                        isMissingClockOut ||
                                        isLessThanTwoHours ||
                                        isMoreThanTenHours;


                                        const log =
                                          getLogForEntry(
                                            logs,
                                            entry?._id
                                          );


                                        const clockInLocation =
                                          log?.clockIn?.location;

                                        const clockOutLocation =
                                          log?.clockOut?.location;


                                        const hasClockInLocation =
                                          clockInLocation &&
                                          clockInLocation.latitude !==
                                            null &&
                                          clockInLocation.latitude !==
                                            undefined &&
                                          clockInLocation.longitude !==
                                            null &&
                                          clockInLocation.longitude !==
                                            undefined;


                                        const hasClockOutLocation =
                                          clockOutLocation &&
                                          clockOutLocation.latitude !==
                                            null &&
                                          clockOutLocation.latitude !==
                                            undefined &&
                                          clockOutLocation.longitude !==
                                            null &&
                                          clockOutLocation.longitude !==
                                            undefined;


                                        const isDetailsExpanded =
                                          expandedDetails[
                                            entry?._id
                                          ] === true;


                                        return (

                                          <React.Fragment
                                            key={
                                              entry._id ||
                                              `${dateKey}-${entryIndex}`
                                            }
                                          >


                                            {/* ==============================
                                                TIME ROW
                                            ============================== */}

                                            <div
                                              onClick={
                                                () => {

                                                  if (
                                                    log
                                                  ) {

                                                    toggleDetails(
                                                      entry?._id
                                                    );

                                                  }

                                                }
                                              }
                                              className={`grid grid-cols-2 md:grid-cols-[1.2fr_1.4fr_0.7fr_1.2fr_100px] gap-x-3 gap-y-2 md:gap-3 px-3 sm:px-5 py-3 border-b border-gray-200 items-center transition ${     log
                                                  ? "cursor-pointer hover:bg-gray-50"
                                                  : ""
                                              } ${
                                                isFlagged
                                                  ? "bg-red-50 hover:bg-red-100"
                                                  : "bg-white"
                                              }`}
                                            >


                                              {/* DATE */}

                                              <div className="flex items-center gap-2 min-w-0">

                                                <span className="md:hidden text-[10px] uppercase tracking-wide text-gray-400 shrink-0">
                                                  Date
                                                </span>

                                                {entryIndex === 0 ? (

                                                  <span className={`font-bold text-base ${
                                                    isFlagged
                                                      ? "text-red-700"
                                                      : "text-gray-800"
                                                  }`}>

                                                    {
                                                      formatDateKeyWithDay(
                                                        dateKey
                                                      )
                                                    }

                                                  </span>

                                                ) : (

                                                  <span className="text-sm text-gray-400">

                                                    {
                                                      entryIndex +
                                                      1
                                                    }

                                                  </span>

                                                )}

                                              </div>


                                              {/* TIME */}

                                              <div className={`text-base min-w-0 ${
                                                isFlagged
                                                  ? "text-red-700 font-semibold"
                                                  : "text-gray-700"
                                              }`}>

                                                <span className="md:hidden block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                                                  Time
                                                </span>

                                                <span className="truncate">
                                                  {formatTime(entry.clockIn)}
                                                  <span className="mx-1 sm:mx-2 text-gray-400">–</span>
                                                  {formatTime(entry.clockOut)}
                                                </span>

                                              </div>


                                              {/* HOURS */}

                                              <div>
                                                <span className="md:hidden block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                                                  Hours
                                                </span>

                                                <span className={`text-base font-bold ${
                                                  isFlagged
                                                    ? "text-red-600"
                                                    : "text-gray-800"
                                                }`}>

                                                  {
                                                    entryHours
                                                  }

                                                </span>

                                              </div>


                                              {/* STORE */}

                                              <div className={`text-base min-w-0 ${
                                                isFlagged
                                                  ? "text-red-700"
                                                  : "text-gray-600"
                                              }`}>

                                                <span className="md:hidden block text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">
                                                  Store
                                                </span>

                                                <span className="truncate block">
                                                  {getStoreName(entry.store)}
                                                </span>

                                              </div>


                                              {/* ACTIONS */}

                                              <div
                                                className="col-span-2 md:col-span-1 flex items-center justify-end md:justify-start gap-2 pt-1 md:pt-0 border-t border-gray-100 md:border-0"
                                                onClick={
                                                  (event) =>
                                                    event.stopPropagation()
                                                }
                                              >


                                                {/* EDIT */}

                                                <button
                                                  type="button"
                                                  onClick={
                                                    () =>
                                                      openEditTimeCard(
                                                        employeeId,
                                                        entry
                                                      )
                                                  }
                                                  disabled={
                                                    deletingEntryId ===
                                                    entry._id
                                                  }
                                                  className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition disabled:opacity-50"
                                                  title="Edit time"
                                                >

                                                  <EditIcon />

                                                </button>


                                                {/* DELETE */}

                                                <button
                                                  type="button"
                                                  onClick={
                                                    () =>
                                                      deleteTimeCard(
                                                        employeeId,
                                                        entry._id
                                                      )
                                                  }
                                                  disabled={
                                                    deletingEntryId ===
                                                    entry._id
                                                  }
                                                  className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                                                  title="Delete time"
                                                >

                                                  {
                                                    deletingEntryId ===
                                                    entry._id
                                                      ? "..."
                                                      : <DeleteIcon />
                                                  }

                                                </button>

                                              </div>

                                            </div>


                                            {/* ==============================
                                                GPS DETAILS
                                            ============================== */}

                                            {log &&
                                              isDetailsExpanded && (

                                                <div className="px-3 sm:px-5 py-4 sm:py-5 bg-gray-50 border-b border-gray-200">

                                                  <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-5">


                                                    {/* HEADER */}

                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">

                                                      <div>

                                                        <h5 className="font-bold text-gray-800">

                                                          GPS & Time Card Details

                                                        </h5>

                                                        <p className="text-xs text-gray-500 mt-1">

                                                          Entry ID:{" "}

                                                          {
                                                            log.entryId ||
                                                            entry._id
                                                          }

                                                        </p>

                                                      </div>


                                                      <span
                                                        className={`inline-flex w-fit items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                                          log.isClockedOut
                                                            ? "bg-green-100 text-green-700"
                                                            : "bg-yellow-100 text-yellow-700"
                                                        }`}
                                                      >

                                                        {
                                                          log.isClockedOut
                                                            ? "Clocked Out"
                                                            : "Still Clocked In"
                                                        }

                                                      </span>

                                                    </div>


                                                    {/* GENERAL INFO */}

                                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">

                                                      <div>

                                                        <p className="text-xs uppercase tracking-wide text-gray-500">

                                                          Store

                                                        </p>

                                                        <p className="text-sm font-medium text-gray-800 mt-1">

                                                          {
                                                            log.store?.name ||
                                                            getStoreName(
                                                              entry.store
                                                            )
                                                          }

                                                        </p>

                                                      </div>


                                                      <div>

                                                        <p className="text-xs uppercase tracking-wide text-gray-500">

                                                          Selection

                                                        </p>

                                                        <p className="text-sm font-medium text-gray-800 mt-1">

                                                          {
                                                            log.storeSelectionLabel ||
                                                            log.storeSelection ||
                                                            "-"
                                                          }

                                                        </p>

                                                      </div>


                                                      <div>

                                                        <p className="text-xs uppercase tracking-wide text-gray-500">

                                                          Hourly Rate

                                                        </p>

                                                        <p className="text-sm font-medium text-gray-800 mt-1">

                                                          {
                                                            log.hourlyRate !==
                                                              undefined &&
                                                            log.hourlyRate !==
                                                              null
                                                              ? `$${Number(
                                                                  log.hourlyRate
                                                                ).toFixed(
                                                                  2
                                                                )}`
                                                              : "-"
                                                          }

                                                        </p>

                                                      </div>


                                                      <div>

                                                        <p className="text-xs uppercase tracking-wide text-gray-500">

                                                          Status

                                                        </p>

                                                        <p className="text-sm font-medium text-gray-800 mt-1">

                                                          {
                                                            log.isClockedOut
                                                              ? "Completed"
                                                              : "Open"
                                                          }

                                                        </p>

                                                      </div>

                                                    </div>


                                                    {/* CLOCK IN / OUT */}

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">


                                                      {/* CLOCK IN */}

                                                      <div className="border border-gray-200 rounded-xl p-5">

                                                        <h6 className="font-bold text-gray-800 mb-4">

                                                          Clock In

                                                        </h6>

                                                        <div className="grid grid-cols-2 gap-4 text-sm">

                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Time

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                log.clockIn?.time
                                                                  ? formatTime(
                                                                      log.clockIn.time
                                                                    )
                                                                  : formatTime(
                                                                      entry.clockIn
                                                                    )
                                                              }

                                                            </p>

                                                          </div>


                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Distance

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                formatDistance(
                                                                  log.clockIn?.distanceFromStore
                                                                )
                                                              }

                                                            </p>

                                                          </div>


                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Latitude

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                hasClockInLocation
                                                                  ? formatCoordinate(
                                                                      clockInLocation.latitude
                                                                    )
                                                                  : "-"
                                                              }

                                                            </p>

                                                          </div>


                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Longitude

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                hasClockInLocation
                                                                  ? formatCoordinate(
                                                                      clockInLocation.longitude
                                                                    )
                                                                  : "-"
                                                              }

                                                            </p>

                                                          </div>

                                                        </div>

                                                      </div>


                                                      {/* CLOCK OUT */}

                                                      <div className="border border-gray-200 rounded-xl p-5">

                                                        <h6 className="font-bold text-gray-800 mb-4">

                                                          Clock Out

                                                        </h6>

                                                        <div className="grid grid-cols-2 gap-4 text-sm">

                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Time

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                log.clockOut?.time
                                                                  ? formatTime(
                                                                      log.clockOut.time
                                                                    )
                                                                  : entry.clockOut
                                                                    ? formatTime(
                                                                        entry.clockOut
                                                                      )
                                                                    : "-"
                                                              }

                                                            </p>

                                                          </div>


                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Distance

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                formatDistance(
                                                                  log.clockOut?.distanceFromStore
                                                                )
                                                              }

                                                            </p>

                                                          </div>


                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Latitude

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                hasClockOutLocation
                                                                  ? formatCoordinate(
                                                                      clockOutLocation.latitude
                                                                    )
                                                                  : "-"
                                                              }

                                                            </p>

                                                          </div>


                                                          <div>

                                                            <p className="text-xs text-gray-500">

                                                              Longitude

                                                            </p>

                                                            <p className="font-medium mt-1">

                                                              {
                                                                hasClockOutLocation
                                                                  ? formatCoordinate(
                                                                      clockOutLocation.longitude
                                                                    )
                                                                  : "-"
                                                              }

                                                            </p>

                                                          </div>

                                                        </div>

                                                      </div>

                                                    </div>

                                                  </div>

                                                </div>

                                              )}

                                          </React.Fragment>

                                        );

                                      }
                                    )}

                                  </React.Fragment>

                                );

                              }
                            )}

                          </div>

                        )}

                      </div>

                    );

                  }
                )}

              </div>

            )}

        </div>

      </div>


      {/* ======================================================
          ADD / EDIT TIME MODAL
      ====================================================== */}

      {timeCardModal.isOpen && (

        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-5">


          {/* BACKDROP */}

          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={closeTimeCardModal}
          />


          {/* MODAL */}

          <div className="relative w-full max-w-5xl max-h-[96dvh] sm:max-h-[92dvh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-gray-200">


            {/* HEADER */}

            <div className="flex items-center justify-between gap-3 px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200">

              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">

                {
                  timeCardModal.mode === "add"
                    ? "Add Time"
                    : "Edit Time"
                }

              </h2>


              <div className="flex items-center gap-3 sm:gap-8">

                {timeCardModal.date && (

                  <div className="flex items-center gap-2">

                    <span className="text-sm sm:text-xl text-gray-500">

                      Hours

                    </span>

                    <span className="text-xl sm:text-2xl font-bold text-gray-800">
  {timeCardModal.endHour &&
  timeCardModal.endMinute !== "" &&
  timeCardModal.endPeriod
    ? minutesToHours(
        calculateMinutes(
          chicagoDateTimeLocalToISO(
            buildDateTimeLocal(
              timeCardModal.date,
              timeCardModal.startHour,
              timeCardModal.startMinute,
              timeCardModal.startPeriod
            )
          ),
          chicagoDateTimeLocalToISO(
            buildDateTimeLocal(
              timeCardModal.date,
              timeCardModal.endHour,
              timeCardModal.endMinute,
              timeCardModal.endPeriod
            )
          )
        )
      )
    : "—"}
</span>

                  </div>

                )}


                <button
                  type="button"
                  onClick={closeTimeCardModal}
                  disabled={isSavingTimeCard}
                  className="text-gray-500 hover:text-gray-900 transition disabled:opacity-50 text-3xl font-light leading-none"
                  aria-label="Close"
                >

                  ×

                </button>

              </div>

            </div>


            {/* FORM */}

            <div className="px-4 sm:px-8 py-5 sm:py-7">

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">


                {/* DATE */}

                <div>

                  <label className="block mb-2 text-sm font-medium uppercase tracking-[0.18em] text-gray-500">

                    Date

                  </label>

                  <input
                    type="date"
                    value={timeCardModal.date}
                    onChange={
                      (e) => {

                        setTimeCardModal(
                          (previous) => ({
                            ...previous,
                            date:
                              e.target.value,
                          })
                        );

                      }
                    }
                    disabled={isSavingTimeCard}
                    className="w-full h-14 sm:h-[68px] px-3 sm:px-5 border border-gray-300 rounded-xl text-base sm:text-xl text-gray-800 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
                  />

                </div>


                {/* START */}

                <div>

                  <label className="block mb-2 text-sm font-medium uppercase tracking-[0.18em] text-gray-500">

                    Start

                  </label>

                  <select
                    value={
                      getTimeSelectValue(

                        timeCardModal.startHour,

                        timeCardModal.startMinute,

                        timeCardModal.startPeriod

                      )
                    }
                    onChange={
                      (e) => {

                        const [
                          hour,
                          minute,
                          period,
                        ] =
                          e.target.value.split(
                            "|"
                          );

                        setTimeCardModal(
                          (previous) => ({
                            ...previous,
                            startHour: hour,
                            startMinute: minute,
                            startPeriod: period,
                          })
                        );

                      }
                    }
                    disabled={isSavingTimeCard}
                    className="w-full h-14 sm:h-[68px] px-3 sm:px-5 border border-gray-300 rounded-xl text-base sm:text-xl text-gray-800 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
                  >

                    {TIME_SELECT_OPTIONS.map(
                      (time) => (

                        <option
                          key={time.value}
                          value={time.value}
                        >

                          {time.label}

                        </option>

                      )
                    )}

                  </select>

                </div>


             {/* END */}

<div>

<label className="block mb-2 text-sm font-medium uppercase tracking-[0.18em] text-gray-500">

  End

</label>


<select
  value={
    getTimeSelectValue(

      timeCardModal.endHour,

      timeCardModal.endMinute,

      timeCardModal.endPeriod

    )
  }
  onChange={
    (e) => {

      const value =
        e.target.value;


      // ================================================
      // OPEN ENTRY
      // ================================================
      //
      // Empty value means the employee has not clocked
      // out yet.
      // ================================================

      if (
        !value
      ) {

        setTimeCardModal(
          (previous) => ({

            ...previous,

            endHour: "",
            endMinute: "",
            endPeriod: "",

          })
        );

        return;

      }


      const [
        hour,
        minute,
        period,
      ] =
        value.split("|");


      setTimeCardModal(
        (previous) => ({

          ...previous,

          endHour:
            hour,

          endMinute:
            minute,

          endPeriod:
            period,

        })
      );

    }
  }
  disabled={
    isSavingTimeCard
  }
  className="w-full h-14 sm:h-[68px] px-3 sm:px-5 border border-gray-300 rounded-xl text-base sm:text-xl text-gray-800 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
>

  {/* ================================================
      OPEN / NOT CLOCKED OUT
  ================================================ */}

  <option value="">

    Not clocked out

  </option>


  {/* ================================================
      NORMAL TIME OPTIONS
  ================================================ */}

  {TIME_SELECT_OPTIONS.map(
    (time) => (

      <option
        key={time.value}
        value={time.value}
      >

        {time.label}

      </option>

    )
  )}

</select>

</div>

              </div>


              {/* STORE */}

              <div className="mt-5 sm:mt-7">

                <label className="block mb-2 text-sm font-medium uppercase tracking-[0.18em] text-gray-500">

                  Store

                </label>

                <select
                  value={
                    timeCardModal.storeId
                  }
                  onChange={
                    (e) => {

                      setTimeCardModal(
                        (previous) => ({
                          ...previous,
                          storeId:
                            e.target.value,
                        })
                      );

                    }
                  }
                  disabled={
                    isSavingTimeCard ||
                    storesLoading
                  }
                  className="w-full h-[68px] px-5 border border-gray-300 rounded-xl text-xl text-gray-800 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-50"
                >

                  <option value="">

                    {
                      storesLoading
                        ? "Loading stores..."
                        : "Select a store"
                    }

                  </option>


                  {stores.map(
                    (store) => {

                      const storeId =
                        store?._id ||
                        store?.id;

                      const storeName =
                        store?.name ||
                        store?.storeName ||
                        "Unnamed Store";

                      return (

                        <option
                          key={storeId}
                          value={storeId}
                        >

                          {storeName}

                        </option>

                      );

                    }
                  )}

                </select>

              </div>

            </div>


            {/* FOOTER */}

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-4 px-4 sm:px-8 py-4 sm:py-5 bg-gray-50 border-t border-gray-200">

              <button
                type="button"
                onClick={closeTimeCardModal}
                disabled={isSavingTimeCard}
                className="w-full sm:w-auto min-w-[140px] px-5 sm:px-7 py-3 border border-gray-300 rounded-xl bg-white text-base sm:text-lg font-semibold text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
              >

                Cancel

              </button>


              <button
                type="button"
                onClick={saveTimeCard}
                disabled={isSavingTimeCard}
                className="w-full sm:w-auto min-w-[200px] px-5 sm:px-7 py-3 rounded-xl bg-slate-800 text-white text-base sm:text-lg font-semibold hover:bg-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >

                {
                  isSavingTimeCard
                    ? "Saving..."
                    : timeCardModal.mode === "add"
                      ? "Add Time"
                      : "Save Changes"
                }

              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );

};


export default AdminTimeReportRead;